import { Link } from 'react-router-dom';
import type { VideoArticle } from '@/mocks/videos';

interface VideoCardProps {
  video: VideoArticle;
  variant?: 'default' | 'compact' | 'featured';
}

export default function VideoCard({ video, variant = 'default' }: VideoCardProps) {
  const formatViews = (views: number): string => {
    if (views >= 10000) {
      return `${(views / 10000).toFixed(1)}万`;
    }
    return views.toString();
  };

  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${video.slug}`}
        className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300"
      >
        <div className="relative overflow-hidden">
          <img
            src={video.thumbnail}
            alt={video.title}
            className="w-full aspect-video object-cover transition-transform duration-500 group-hover:scale-105"
            title={`${video.title} - 蜜桃tv`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent"></div>
          <div className="absolute bottom-3 left-3 right-3">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 rounded-md bg-accent-500 text-background-50 text-xs font-medium">
                {video.category}
              </span>
              <span className="px-2 py-0.5 rounded-md bg-black/40 text-background-50 text-xs">
                {video.duration}
              </span>
            </div>
          </div>
        </div>
        <div className="p-4">
          <h3 className="text-base font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 mb-2">
            {video.title}
          </h3>
          <p className="text-sm text-foreground-600 line-clamp-2 mb-3">
            {video.summary}
          </p>
          <div className="flex items-center justify-between text-xs text-foreground-500">
            <div className="flex items-center gap-3">
              <span className="flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {formatViews(video.views)}
              </span>
              <span className="flex items-center gap-1">
                <i className="ri-heart-line"></i>
                {formatViews(video.likes)}
              </span>
            </div>
            <span className="flex items-center gap-1">
              <i className="ri-star-fill text-accent-500"></i>
              {video.rating}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${video.slug}`}
        className="group flex gap-3 p-2 rounded-lg hover:bg-background-100 transition-colors duration-200"
      >
        <div className="relative w-40 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={video.thumbnail}
            alt={video.title}
            className="w-full aspect-video object-cover transition-transform duration-500 group-hover:scale-105"
            title={`${video.title} - 蜜桃tv`}
          />
          <span className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/60 text-background-50 text-xs">
            {video.duration}
          </span>
        </div>
        <div className="flex-1 min-w-0 py-1">
          <h4 className="text-sm font-medium text-foreground-950 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 mb-1">
            {video.title}
          </h4>
          <p className="text-xs text-foreground-500 line-clamp-1 mb-2">
            {video.summary}
          </p>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-700 text-xs">
              {video.category}
            </span>
            <span>{formatViews(video.views)} 次观看</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${video.slug}`}
      className="group block rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-300 transition-all duration-300"
    >
      <div className="relative overflow-hidden">
        <img
          src={video.thumbnail}
          alt={video.title}
          className="w-full aspect-video object-cover transition-transform duration-500 group-hover:scale-105"
          title={`${video.title} - 蜜桃tv`}
        />
        <span className="absolute bottom-2 right-2 px-2 py-0.5 rounded bg-black/60 text-background-50 text-xs">
          {video.duration}
        </span>
        {video.isNew && (
          <span className="absolute top-2 left-2 px-2 py-0.5 rounded bg-accent-500 text-background-50 text-xs font-medium">
            新
          </span>
        )}
      </div>
      <div className="p-3">
        <h3 className="text-sm font-semibold text-foreground-950 group-hover:text-primary-600 transition-colors duration-200 line-clamp-2 mb-1.5">
          {video.title}
        </h3>
        <div className="flex items-center justify-between text-xs text-foreground-500">
          <span>{formatViews(video.views)} 次观看</span>
          <span className="flex items-center gap-1">
            <i className="ri-star-fill text-accent-500 text-xs"></i>
            {video.rating}
          </span>
        </div>
      </div>
    </Link>
  );
}